import type { AiConfig, AnalyticsSummary, ChatbotAccess, ChatbotDetail, ChatbotSection, ChatbotSettings, ChatMessage, ConversationDetail, ConversationList, DiscoveryResult, KnowledgeFile, PublicWidgetSettings, SearchDomain, SearchResult, TextSearch } from "@/lib/types"
import { CHATBOT_SECTIONS } from "@/lib/types"

export type AuthUser = { id: string; name: string; email: string; role: "admin" | "superadmin" }

export type ManagedAccount = {
  id: string
  name: string
  email: string
  role?: string
  banned?: boolean | null
  banReason?: string | null
  createdAt: string
}

export type ChatbotMember = {
  id: string
  userId: string
  name: string
  email: string
  joinedAt: string
  permissions: ChatbotSection[]
}

export type ChatbotInvitation = {
  id: string
  email: string
  status: "pending" | "accepted" | "canceled" | "expired"
  expiresAt: string
  createdAt?: string
  permissions: ChatbotSection[]
}

export type ChatbotMembersOverview = {
  chatbotId: string
  chatbotName: string
  owner: { id: string; name: string; email: string }
  isOwner: boolean
  /** Id de quem está vendo: a própria linha não oferece edição de acesso. */
  viewerId: string
  canManage: boolean
  access: ChatbotAccess
  members: ChatbotMember[]
  invitations: ChatbotInvitation[]
}

export type ChatbotInvitationPreview = {
  id: string
  email: string
  status: "pending" | "accepted" | "canceled" | "expired"
  expiresAt: string
  permissions: ChatbotSection[]
  chatbotId: string
  chatbotName: string
}

export type GenesysIntegrationStatus = {
  provider: "genesys"
  status: "disabled" | "active" | "degraded" | "error"
  configured: boolean
  region: string | null
  regions: Array<{ id: string; label: string }>
  routingIntegrationId: string | null
  routingQueueValue: string
  webhookUrl: string | null
  oauthClientIdConfigured: boolean
  oauthClientSecretConfigured: boolean
  oauthClientConfigured: boolean
  webhookSecretConfigured: boolean
  lastVerifiedAt: string | null
  lastError: { code: string; at: string | null } | null
  lastHandoffError: { code: string; status: string; at: string } | null
  canManage: boolean
}

export type GenesysIntegrationInput = {
  region: string
  routingIntegrationId: string
  routingQueueValue: string
  oauthClientId?: string
  oauthClientSecret?: string
  webhookSecret?: string
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = "ApiError"
  }
}

type ResponsePayload = { parsed: boolean; value: unknown }

type RecordValue = Record<string, unknown>

function isRecord(value: unknown): value is RecordValue {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isRecordArray(value: unknown): value is RecordValue[] {
  return Array.isArray(value) && value.every(isRecord)
}

// Payload legado (servidor sem o campo `access`) continua sendo acesso total.
export function parseChatbotAccess(value: unknown): ChatbotAccess {
  if (!isRecord(value) || !Array.isArray(value.sections)) {
    return { isOwner: true, isSuperadmin: false, sections: [...CHATBOT_SECTIONS] }
  }
  const sections = value.sections as unknown[]
  return {
    isOwner: value.isOwner === true,
    isSuperadmin: value.isSuperadmin === true,
    sections: CHATBOT_SECTIONS.filter((section) => sections.includes(section)),
  }
}

function isChatbotDetailResponse(value: unknown): value is ChatbotDetail {
  if (!isRecord(value)) return false
  const access = parseChatbotAccess(value.access)
  const requiredStrings = ["id", "publicKey", "ownerId", "name", "assistantName", "chatTitle", "chatSubtitle", "welcomeMessage", "placeholder"] as const
  if (!requiredStrings.every((key) => typeof value[key] === "string")) return false
  if (!isRecord(value.stats)) return false
  // Cada seção responde pelos próprios campos: um convidado sem "ai" não recebe
  // systemPrompt/perguntas, e isso não pode virar erro de payload inválido.
  if (access.sections.includes("ai")) {
    if (typeof value.systemPrompt !== "string") return false
    if (!Array.isArray(value.suggestedQuestions) || !value.suggestedQuestions.every((question) => typeof question === "string")) return false
    if (!Array.isArray(value.quickActions)) return false
  }
  if (access.sections.includes("installation") && !isRecordArray(value.installDomains)) return false
  if (access.sections.includes("content")) {
    if (!isRecordArray(value.files) || !isRecordArray(value.textSearches) || !isRecord(value.textSearchCounts)) return false
    return isRecordArray(value.domains) && value.domains.every((domain) =>
      typeof domain.id === "string"
      && typeof domain.hostname === "string"
      && isRecordArray(domain.pages)
      && isRecordArray(domain.rules)
      && isRecordArray(domain.files)
      && isRecordArray(domain.textSearches)
    )
  }
  return true
}

// Toda transição de estado do editor passa por aqui: se o servidor devolver um
// payload degradado (proxy, serializer antigo, resposta parcial), falhamos com
// ApiError em vez de deixar o render quebrar em `detail.installDomains.length`
// ou `detail.files.filter`. Depois da validação, garantimos as coleções para
// que nenhum acesso no render dependa de um array que o backend esqueceu.
function normalizeChatbotDetail(value: ChatbotDetail): ChatbotDetail {
  return {
    ...value,
    ...(value.access ? { access: parseChatbotAccess(value.access) } : {}),
    suggestedQuestions: Array.isArray(value.suggestedQuestions) ? value.suggestedQuestions : [],
    quickActions: Array.isArray(value.quickActions) ? value.quickActions : [],
    aiModels: Array.isArray(value.aiModels) ? value.aiModels : [],
    installDomains: Array.isArray(value.installDomains) ? value.installDomains : [],
    files: Array.isArray(value.files) ? value.files : [],
    textSearches: Array.isArray(value.textSearches) ? value.textSearches : [],
    textSearchCounts: isRecord(value.textSearchCounts) ? value.textSearchCounts : {},
    domains: Array.isArray(value.domains)
      ? value.domains.map((domain) => ({
        ...domain,
        manualUrls: Array.isArray(domain.manualUrls) ? domain.manualUrls : [],
        rules: Array.isArray(domain.rules) ? domain.rules : [],
        pages: Array.isArray(domain.pages) ? domain.pages : [],
        files: Array.isArray(domain.files) ? domain.files : [],
        textSearches: Array.isArray(domain.textSearches) ? domain.textSearches : [],
      }))
      : [],
  }
}

function ensureChatbotDetail(value: unknown): ChatbotDetail {
  if (!isChatbotDetailResponse(value)) {
    throw new ApiError("O servidor retornou dados inválidos para este assistente.", 502)
  }
  return normalizeChatbotDetail(value)
}

function ensureGenesysIntegrationStatus(value: unknown): GenesysIntegrationStatus {
  const allowedKeys = new Set([
    "provider", "status", "configured", "region", "regions", "routingIntegrationId", "routingQueueValue", "webhookUrl", "oauthClientConfigured",
    "oauthClientIdConfigured", "oauthClientSecretConfigured", "webhookSecretConfigured", "lastVerifiedAt", "lastError", "lastHandoffError", "canManage",
  ])
  if (!isRecord(value) || Object.keys(value).some((key) => !allowedKeys.has(key))) {
    throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
  }
  if (
    value.provider !== "genesys" ||
    !["disabled", "active", "degraded", "error"].includes(String(value.status)) ||
    typeof value.configured !== "boolean" ||
    !(typeof value.region === "string" || value.region === null) ||
    !Array.isArray(value.regions) || value.regions.some((region) =>
      !isRecord(region) || Object.keys(region).some((key) => key !== "id" && key !== "label") ||
      typeof region.id !== "string" || typeof region.label !== "string"
    ) ||
    !(typeof value.routingIntegrationId === "string" || value.routingIntegrationId === null) ||
    typeof value.routingQueueValue !== "string" ||
    !(typeof value.webhookUrl === "string" || value.webhookUrl === null) ||
    typeof value.oauthClientIdConfigured !== "boolean" ||
    typeof value.oauthClientSecretConfigured !== "boolean" ||
    typeof value.oauthClientConfigured !== "boolean" ||
    typeof value.webhookSecretConfigured !== "boolean" ||
    !(typeof value.lastVerifiedAt === "string" || value.lastVerifiedAt === null) ||
    typeof value.canManage !== "boolean"
  ) throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
  let lastError: GenesysIntegrationStatus["lastError"] = null
  if (value.lastError !== null) {
    if (!isRecord(value.lastError) || Object.keys(value.lastError).some((key) => key !== "code" && key !== "at")) {
      throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
    }
    if (typeof value.lastError.code !== "string" || !(typeof value.lastError.at === "string" || value.lastError.at === null)) {
      throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
    }
    lastError = { code: value.lastError.code, at: value.lastError.at }
  }
  let lastHandoffError: GenesysIntegrationStatus["lastHandoffError"] = null
  if (value.lastHandoffError !== null) {
    if (!isRecord(value.lastHandoffError) || Object.keys(value.lastHandoffError).some((key) => key !== "code" && key !== "status" && key !== "at")) {
      throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
    }
    if (typeof value.lastHandoffError.code !== "string" || typeof value.lastHandoffError.status !== "string" || typeof value.lastHandoffError.at !== "string") {
      throw new ApiError("O servidor retornou dados inválidos para a integração Genesys.", 502)
    }
    lastHandoffError = { code: value.lastHandoffError.code, status: value.lastHandoffError.status, at: value.lastHandoffError.at }
  }
  return {
    provider: "genesys",
    status: value.status as GenesysIntegrationStatus["status"],
    configured: value.configured,
    region: value.region,
    regions: value.regions,
    routingIntegrationId: value.routingIntegrationId,
    routingQueueValue: value.routingQueueValue,
    webhookUrl: value.webhookUrl,
    oauthClientIdConfigured: value.oauthClientIdConfigured,
    oauthClientSecretConfigured: value.oauthClientSecretConfigured,
    oauthClientConfigured: value.oauthClientConfigured,
    webhookSecretConfigured: value.webhookSecretConfigured,
    lastVerifiedAt: value.lastVerifiedAt,
    lastError,
    lastHandoffError,
    canManage: value.canManage,
  }
}

async function requestChatbotDetail(url: string, options?: RequestInit): Promise<ChatbotDetail> {
  return ensureChatbotDetail(await request<unknown>(url, options))
}

async function readResponsePayload(response: Response): Promise<ResponsePayload> {
  const text = await response.text().catch(() => "")
  if (!text.trim()) return { parsed: true, value: undefined }
  try {
    return { parsed: true, value: JSON.parse(text) as unknown }
  } catch {
    return { parsed: false, value: text }
  }
}

function responseErrorMessage(payload: unknown, status: number) {
  if (typeof payload === "object" && payload !== null) {
    const record = payload as { error?: unknown; message?: unknown }
    if (typeof record.error === "string" && record.error.trim()) return record.error
    if (typeof record.message === "string" && record.message.trim()) return record.message
  }
  return status >= 500 ? "O servidor não conseguiu processar a solicitação." : "Não foi possível concluir a ação."
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const isMultipart = typeof FormData !== "undefined" && options?.body instanceof FormData
  let response: Response
  try {
    response = await fetch(url, {
      ...options,
      headers: isMultipart ? options?.headers : { "content-type": "application/json", ...options?.headers },
    })
  } catch {
    throw new ApiError("Não foi possível conectar ao servidor.", 0)
  }

  const payload = await readResponsePayload(response)
  if (!response.ok) {
    throw new ApiError(payload.parsed ? responseErrorMessage(payload.value, response.status) : responseErrorMessage(undefined, response.status), response.status)
  }
  if (response.status === 204) return undefined as T
  if (!payload.parsed) throw new ApiError("O servidor retornou uma resposta inválida. Tente novamente.", response.status)
  return payload.value as T
}

const MAX_KNOWLEDGE_FILE_BYTES = 20 * 1024 * 1024
const KNOWLEDGE_FILE_PATTERN = /\.(pdf|txt|md|markdown)$/i

function normalizeUploadDomainId(domainId?: string | null) {
  const normalized = domainId?.trim()
  return normalized && normalized.toLowerCase() !== "global" ? normalized : null
}

export interface ChatbotListItem extends ChatbotSettings {
  ownerEmail: string | null
  access?: ChatbotAccess
  stats: { domains: number; conversations: number; pages: number; installDomains: number; primaryDomain: string | null }
}

function toAuthUser(rawUser: { id: string; name?: string; email: string; role?: string }): AuthUser {
  const fallbackName = rawUser.email.split("@")[0] ?? "Conta"
  return {
    id: rawUser.id,
    name: rawUser.name?.trim() || fallbackName,
    email: rawUser.email,
    role: rawUser.role === "superadmin" ? "superadmin" : "admin",
  }
}

export const api = {
  login: async (email: string, password: string) => {
    const result = await request<{ user: { id: string; name?: string; email: string; role?: string } }>("/api/auth/sign-in/email", { method: "POST", body: JSON.stringify({ email, password, rememberMe: true }) })
    return toAuthUser(result.user)
  },
  signup: async (name: string, email: string, password: string) => {
    const result = await request<{ user: { id: string; name?: string; email: string; role?: string } }>("/api/auth/sign-up/email", { method: "POST", body: JSON.stringify({ name, email, password }) })
    return toAuthUser(result.user)
  },
  requestPasswordReset: (email: string) => request<{ status: boolean; message: string }>("/api/auth/request-password-reset", {
    method: "POST",
    body: JSON.stringify({ email, redirectTo: `${window.location.origin}/password-reset` }),
  }),
  resetPassword: (token: string, newPassword: string) => request<{ status: boolean }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  }),
  me: async () => {
    const result = await request<{ user?: { id: string; name?: string; email: string; role?: string } } | null>("/api/auth/get-session")
    if (!result?.user) throw new ApiError("Autenticação necessária.", 401)
    return toAuthUser(result.user)
  },
  logout: () => request<void>("/api/auth/sign-out", { method: "POST" }),
  getChatbotMembers: (id: string) => request<ChatbotMembersOverview>(`/api/chatbots/${id}/members`),
  inviteChatbotMember: (id: string, email: string, permissions: ChatbotSection[]) => request<{ invitation: ChatbotInvitation }>(`/api/chatbots/${id}/members/invitations`, {
    method: "POST",
    body: JSON.stringify({ email, permissions }),
  }),
  updateChatbotMemberPermissions: (id: string, userId: string, permissions: ChatbotSection[]) => request<{ userId: string; permissions: ChatbotSection[] }>(`/api/chatbots/${id}/members/${userId}/permissions`, {
    method: "PUT",
    body: JSON.stringify({ permissions }),
  }),
  cancelChatbotInvitation: (id: string, invitationId: string) => request<void>(`/api/chatbots/${id}/members/invitations/${invitationId}`, { method: "DELETE" }),
  removeChatbotMember: (id: string, userId: string) => request<void>(`/api/chatbots/${id}/members/${userId}`, { method: "DELETE" }),
  getChatbotInvitation: (token: string) => request<ChatbotInvitationPreview>(`/api/chatbots/invitations/${encodeURIComponent(token)}`),
  acceptChatbotInvitation: (token: string) => request<{ chatbotId: string; chatbotName: string; permissions: ChatbotSection[] }>(`/api/chatbots/invitations/${encodeURIComponent(token)}/accept`, { method: "POST" }),
  listUsers: async (): Promise<ManagedAccount[]> => {
    const result = await request<{ users: ManagedAccount[]; total: number }>("/api/auth/admin/list-users?sortBy=createdAt&sortDirection=desc&limit=200")
    return result.users
  },
  setUserPassword: (userId: string, newPassword: string) => request<{ status: boolean }>("/api/auth/admin/set-user-password", {
    method: "POST",
    body: JSON.stringify({ userId, newPassword }),
  }),
  updateUserName: (userId: string, name: string) => request<{ user: ManagedAccount }>("/api/auth/admin/update-user", {
    method: "POST",
    body: JSON.stringify({ userId, data: { name } }),
  }),
  setUserRole: (userId: string, role: "admin" | "superadmin") => request<{ user: ManagedAccount }>("/api/auth/admin/set-role", {
    method: "POST",
    body: JSON.stringify({ userId, role }),
  }),
  banUser: (userId: string, banReason?: string) => request<{ user: ManagedAccount }>("/api/auth/admin/ban-user", {
    method: "POST",
    body: JSON.stringify({ userId, ...(banReason?.trim() ? { banReason: banReason.trim() } : {}) }),
  }),
  unbanUser: (userId: string) => request<{ user: ManagedAccount }>("/api/auth/admin/unban-user", {
    method: "POST",
    body: JSON.stringify({ userId }),
  }),
  removeUser: (userId: string) => request<{ success: boolean }>("/api/auth/admin/remove-user", {
    method: "POST",
    body: JSON.stringify({ userId }),
  }),
  listChatbots: () => request<ChatbotListItem[]>("/api/chatbots"),
  createChatbot: (name: string) => request<ChatbotDetail>("/api/chatbots", { method: "POST", body: JSON.stringify({ name }) }),
  getChatbot: (id: string) => requestChatbotDetail(`/api/chatbots/${id}`),
  getGenesysIntegration: async (id: string) => ensureGenesysIntegrationStatus(await request<unknown>(`/api/chatbots/${id}/integrations/genesys`)),
  saveGenesysIntegration: async (id: string, input: GenesysIntegrationInput) => ensureGenesysIntegrationStatus(await request<unknown>(`/api/chatbots/${id}/integrations/genesys`, { method: "PUT", body: JSON.stringify(input) })),
  verifyGenesysIntegration: async (id: string) => ensureGenesysIntegrationStatus(await request<unknown>(`/api/chatbots/${id}/integrations/genesys/verify`, { method: "POST", body: JSON.stringify({}) })),
  setGenesysIntegrationStatus: async (id: string, status: "active" | "disabled") => ensureGenesysIntegrationStatus(await request<unknown>(`/api/chatbots/${id}/integrations/genesys/status`, { method: "PATCH", body: JSON.stringify({ status }) })),
  deleteGenesysIntegration: (id: string) => request<void>(`/api/chatbots/${id}/integrations/genesys`, { method: "DELETE" }),
  updateChatbot: (id: string, settings: Partial<ChatbotSettings> & { expectedSuggestedQuestions?: string[]; publishReviewedSuggestedQuestions?: boolean }) => requestChatbotDetail(`/api/chatbots/${id}`, { method: "PUT", body: JSON.stringify(settings) }),
  deleteChatbot: (id: string) => request<void>(`/api/chatbots/${id}`, { method: "DELETE" }),
  addDomain: (id: string, url: string, manualUrls?: string[]) => requestChatbotDetail(`/api/chatbots/${id}/domains`, { method: "POST", body: JSON.stringify({ manualUrls: manualUrls ?? [], url }) }),
  listDomains: (id: string) => request<SearchDomain[]>(`/api/chatbots/${id}/domains`),
  getDomain: (id: string, domainId: string) => request<SearchDomain>(`/api/chatbots/${id}/domains/${domainId}`),
  updateDomain: (id: string, domainId: string, input: { manualUrls?: string[] }) => requestChatbotDetail(`/api/chatbots/${id}/domains/${domainId}`, { method: "PUT", body: JSON.stringify(input) }),
  deleteDomain: (id: string, domainId: string) => request<void>(`/api/chatbots/${id}/domains/${domainId}`, { method: "DELETE" }),
  updateRules: (id: string, domainId: string, allow: string[], block: string[]) => requestChatbotDetail(`/api/chatbots/${id}/domains/${domainId}/rules`, { method: "PUT", body: JSON.stringify({ allow, block }) }),
  discoverPages: async (id: string, domainId: string) => {
    const result = await request<DiscoveryResult & { chatbot: unknown }>(`/api/chatbots/${id}/domains/${domainId}/discover`, { method: "POST" })
    return { ...result, chatbot: ensureChatbotDetail(result.chatbot) }
  },
  updateManualUrls: (id: string, domainId: string, manualUrls: string[]) => requestChatbotDetail(`/api/chatbots/${id}/domains/${domainId}/manual-urls`, { method: "PUT", body: JSON.stringify({ manualUrls }) }),
  togglePage: (id: string, pageId: string, selected: boolean) => requestChatbotDetail(`/api/chatbots/${id}/pages/${pageId}`, { method: "PUT", body: JSON.stringify({ selected }) }),
  testSearch: (id: string, query: string, domainId?: string) => request<{ query: string; count: number; results: SearchResult[]; domainId: string | null }>(`/api/chatbots/${id}/test-search`, { method: "POST", body: JSON.stringify({ query, ...(domainId ? { domainId } : {}) }) }),
  listTextSearches: (id: string, domainId?: string) => request<TextSearch[]>(`/api/chatbots/${id}/text-searches${domainId ? `?domainId=${encodeURIComponent(domainId)}` : ""}`),
  getAiConfig: (id: string) => request<AiConfig>(`/api/chatbots/${id}/ai-config`),
  updateAiConfig: (id: string, config: Partial<Pick<AiConfig, "model" | "systemPrompt">>) => requestChatbotDetail(`/api/chatbots/${id}/ai-config`, { method: "PUT", body: JSON.stringify(config) }),
  updateInstallDomains: (id: string, domains: string[]) => requestChatbotDetail(`/api/chatbots/${id}/install-domains`, { method: "PUT", body: JSON.stringify({ domains }) }),
  updateInstallDomainStatus: (id: string, domainId: string, status: "active" | "paused") => requestChatbotDetail(`/api/chatbots/${id}/install-domains/${domainId}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  previewSession: (id: string) => request<{ token: string }>(`/api/chatbots/${id}/preview-session`, { method: "POST" }),
  analyticsSummary: (id: string, from?: string, to?: string) => request<AnalyticsSummary>(`/api/chatbots/${id}/analytics/summary${from || to ? `?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString()}` : ""}`),
  listConversations: (id: string, params?: { from?: string; to?: string; q?: string; status?: string; page?: number; pageSize?: number }) => request<ConversationList>(`/api/chatbots/${id}/conversations${params ? `?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))).toString()}` : ""}`),
  getConversation: (id: string, conversationId: string) => request<ConversationDetail>(`/api/chatbots/${id}/conversations/${conversationId}`),
  exportConversationsUrl: (id: string, params?: { from?: string; to?: string; q?: string; status?: string; format?: "csv" | "markdown" }) => `/api/chatbots/${id}/conversations/export${params ? `?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))).toString()}` : "?format=csv"}`,
  uploadFile: async (id: string, file: File, domainId?: string | null) => {
    if (!file || file.size <= 0) throw new ApiError("Selecione um arquivo com conteúdo.", 400)
    if (!KNOWLEDGE_FILE_PATTERN.test(file.name)) throw new ApiError("Formato inválido. Envie um arquivo PDF, TXT ou Markdown.", 400)
    if (file.size > MAX_KNOWLEDGE_FILE_BYTES) throw new ApiError("O arquivo excede o limite de 20 MB.", 400)

    const targetDomainId = normalizeUploadDomainId(domainId)
    const body = new FormData()
    body.append("file", file)
    if (targetDomainId) body.append("domainId", targetDomainId)
    const result = await request<unknown>(`/api/chatbots/${id}/files${targetDomainId ? `?domainId=${encodeURIComponent(targetDomainId)}` : ""}`, { method: "POST", body })
    try {
      return ensureChatbotDetail(result)
    } catch {
      throw new ApiError("O servidor retornou dados inválidos após o upload. Tente novamente.", 502)
    }
  },
  listDomainFiles: async (id: string, domainId: string): Promise<KnowledgeFile[]> => {
    const result = await request<unknown>(`/api/chatbots/${id}/domains/${domainId}/files`)
    return isRecordArray(result) ? result as unknown as KnowledgeFile[] : []
  },
  moveFile: (id: string, fileId: string, domainId: string | null) => requestChatbotDetail(`/api/chatbots/${id}/files/${fileId}`, { method: "PUT", body: JSON.stringify({ domainId }) }),
  setFileVisibility: (id: string, fileId: string, visibility: "internal" | "public") => requestChatbotDetail(`/api/chatbots/${id}/files/${fileId}/visibility`, { method: "PATCH", body: JSON.stringify({ visibility }) }),
  generateSuggestedQuestions: (id: string) => requestChatbotDetail(`/api/chatbots/${id}/suggested-questions/generate`, { method: "POST" }),
  deleteFile: (id: string, fileId: string) => request<void>(`/api/chatbots/${id}/files/${fileId}`, { method: "DELETE" }),
  publicConfig: (publicKey: string, sessionToken: string) => request<PublicWidgetSettings>(`/api/public/chatbots/${publicKey}?token=${encodeURIComponent(sessionToken)}`),
  restoreWidgetConversation: (publicKey: string, input: { sessionToken: string; visitorId: string; sessionKey: string; signal?: AbortSignal }) => request<{
    conversation: null | {
      conversationId: string
      messages: ChatMessage[]
      handoffState: "requested" | "dispatching" | "recovering" | "active" | null
      statusMessage?: ChatMessage
      eventCursor: string
    }
  }>(`/api/public/chatbots/${encodeURIComponent(publicKey)}/conversations/current`, {
    method: "GET",
    signal: input.signal,
    headers: {
      "X-Widget-Session-Token": input.sessionToken,
      "X-Widget-Visitor-Id": input.visitorId,
      "X-Widget-Session-Key": input.sessionKey,
    },
  }),
  sendMessage: (publicKey: string, payload: { message: string; conversationId?: string; visitorId: string; sessionKey?: string; pageUrl?: string; sessionToken?: string }) => request<{ conversationId: string; message: { id: string; role: "assistant"; content: string; sources: SearchResult[] } }>(`/api/public/chatbots/${publicKey}/messages`, { method: "POST", body: JSON.stringify(payload) }),

  streamMessage: async function* (
    publicKey: string,
    payload: { message: string; conversationId?: string; visitorId: string; sessionKey?: string; pageUrl?: string; sessionToken?: string },
  ): AsyncGenerator<StreamEvent, void, void> {
    const response = await fetch(`/api/public/chatbots/${publicKey}/messages/stream`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "text/event-stream" },
      body: JSON.stringify(payload),
    })
    if (!response.ok || !response.body) {
      const fallback = await response.text().catch(() => "")
      let message = "Falha na comunicação com o assistente."
      if (fallback) {
        try { message = (JSON.parse(fallback) as { error?: string }).error ?? message } catch { message = fallback || message }
      }
      throw new ApiError(message, response.status || 0)
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let sep = buffer.indexOf("\n\n")
        while (sep >= 0) {
          const raw = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)
          const line = raw.split("\n").find((entry) => entry.startsWith("data:"))
          if (line) {
            const data = line.slice(5).trim()
            if (data && data !== "[DONE]") {
              try { yield JSON.parse(data) as StreamEvent } catch { /* ignora evento malformado */ }
            }
          }
          sep = buffer.indexOf("\n\n")
        }
      }
    } finally {
      reader.releaseLock()
    }
  },

  streamConversationEvents: async function* (
    publicKey: string,
    conversationId: string,
    input: { sessionToken: string; visitorId: string; sessionKey: string; after?: string; signal?: AbortSignal },
  ): AsyncGenerator<ConversationStreamEvent, void, void> {
    const response = await fetch(`/api/public/chatbots/${encodeURIComponent(publicKey)}/conversations/${encodeURIComponent(conversationId)}/events`, {
      method: "GET",
      headers: {
        accept: "text/event-stream",
        "X-Widget-Session-Token": input.sessionToken,
        "X-Widget-Visitor-Id": input.visitorId,
        "X-Widget-Session-Key": input.sessionKey,
        ...(input.after ? { "Last-Event-ID": input.after } : {}),
      },
      signal: input.signal,
    })
    if (!response.ok || !response.body) {
      const fallback = await response.text().catch(() => "")
      let message = "Não foi possível acompanhar a conversa."
      if (fallback) {
        try { message = (JSON.parse(fallback) as { error?: string }).error ?? message } catch { /* mantém erro neutro */ }
      }
      throw new ApiError(message, response.status || 0)
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let separator = buffer.indexOf("\n\n")
        while (separator >= 0) {
          const raw = buffer.slice(0, separator)
          buffer = buffer.slice(separator + 2)
          const id = raw.split("\n").find((line) => line.startsWith("id:"))?.slice(3).trim()
          const data = raw.split("\n").find((line) => line.startsWith("data:"))?.slice(5).trim()
          if (id && data) {
            try {
              yield { ...(JSON.parse(data) as ConversationStreamEvent), sequence: id }
            } catch { /* ignora evento malformado */ }
          }
          separator = buffer.indexOf("\n\n")
        }
      }
    } finally {
      reader.releaseLock()
    }
  },
}

export type StreamEvent =
  | { type: "start"; conversationId: string; messageId: string; sources: SearchResult[] }
  | { type: "delta"; content: string }
  | { type: "handoff_state"; state: "reason_requested" | "requested" | "dispatching" | "recovering" | "active" | "failed" | "ended" | "cancelled"; messageId?: string; content?: string }
  | { type: "done" }
  | { type: "error"; message: string }

export type ConversationStreamEvent =
  | { type: "handoff_state"; state: "reason_requested" | "requested" | "dispatching" | "recovering" | "active" | "failed" | "ended" | "cancelled"; messageId?: string; content?: string; chatbotPaused?: boolean; sequence: string }
  | { type: "agent_message"; messageId: string; authorType: "genesys_agent"; content: string; sequence: string }
  | { type: "queue_message"; messageId: string; authorType: "genesys_automation"; content: string; sequence: string }
  | { type: "queue_status"; queueName: string | null; estimatedWaitTimeSeconds: number | null; sequence: string }
  | { type: "error"; message: string; sequence: string }
